import { type NormalizedLicenseData } from "./normalizeLicenseData"

const ACCESS_CODES: Record<string, string> = {
  accessDirect: "D",
  accessCollaborative: "C",
  accessFederated: "F",
  accessTRE: "T"
}

const pushUnique = (list: string[], code?: string | null) => {
  if (!code) return
  if (list.includes(code)) return
  list.push(code)
}

type BuildLicenseCodeMode = "full" | "sharing" | "terms"

export const buildLicenseCode = (
  data: NormalizedLicenseData,
  mode: BuildLicenseCodeMode = "full"
): string => {
  const sharing: string[] = []

  data.accessMode?.forEach(mode => pushUnique(sharing, ACCESS_CODES[mode]))

  const majors: string[] = []
  if (data.commercialUse === "no") pushUnique(majors, "N") // Non-commercial
  if (data.onwardSharing === "no") pushUnique(majors, "O") // Onward block
  if (data.onwardSharing === "yes") {
    if (data.shareAlike === "yes") pushUnique(majors, "L") // Share-aLike
    if (data.modifications === "no") pushUnique(majors, "X") // No derivatives
  }
  if (data.benefitSharing === "yes") pushUnique(majors, "B") // Benefit-sharing
  if (data.costRecovery === "yes") pushUnique(majors, "C") // Cost recovery

  let publishingSegment = ""
  if (data.publishingPermitted === "no") {
    publishingSegment = "P"
  } else {
    const { publishingRequirements = {} } = data
    const publishingSubs: string[] = []
    const hasReq =
      publishingRequirements.embargo ||
      publishingRequirements.review ||
      publishingRequirements.reporting

    if (hasReq) {
      if (publishingRequirements.embargo) pushUnique(publishingSubs, "e")
      if (publishingRequirements.review) pushUnique(publishingSubs, "v") // vetting/review
      if (publishingRequirements.reporting) pushUnique(publishingSubs, "g") // aggregate reporting
      if (publishingRequirements.reporting && data.reportingRequirements === "yes" && data.reportingTypesPermitted?.trim()) pushUnique(publishingSubs, "t") // text detail
      publishingSegment = `E${publishingSubs.join("")}`
    }
  }

  let attributionSegment = ""
  if (data.attributionRequired === "yes") {
    const attributionSubs: string[] = []
    if (data.attributionRequirements?.attribution) pushUnique(attributionSubs, "a")
    if (data.attributionRequirements?.authorship) pushUnique(attributionSubs, "h")
    attributionSegment = `A${attributionSubs.join("")}`
  }

  const sharingSegment = sharing.join("")
  const tailSegments = [majors.join(""), publishingSegment, attributionSegment].filter(Boolean)
  const tail = tailSegments.join(".")

  if (mode === "sharing") {
    return sharingSegment || "std"
  }

  if (mode === "terms") {
    return tail || "std"
  }

  if (sharingSegment && tail) return `${sharingSegment}-${tail}`
  return sharingSegment || tail || "std"
}
